/**
 * O contexto de EXECUÇÃO das tools de front — `{vm, router, store, canAccessRoute}`
 * (spec-001 `plataforma-por-chat`, [T015] / US-4).
 *
 * ★ Por que um provedor SEPARADO do `contexto-de-tela`, e não um campo a mais
 *   nele: o contexto de tela é despejado no CORPO do `POST /chat`
 *   (`Object.assign(body, this._contextoDeTela())` em `apiChat.js`). Pôr o
 *   `router` e o `vm` ali dentro mandaria uma instância Vue inteira ao
 *   `JSON.stringify` a cada mensagem — no melhor caso um corpo gigante, no pior
 *   um `TypeError: Converting circular structure to JSON` que mata o envio.
 *   São dois contextos com destinos opostos: um viaja ao servidor, o outro
 *   nunca sai do browser.
 *
 * ★ Por que INJEÇÃO e não `import router from '@router'`: o import fecha o
 *   ciclo `@router` → `routes.js` → `_404.vue` (eager) → `layouts/main.vue` →
 *   `fee-fixo-ia-especialista.vue` → `apiFeeFixoIa.js` → `extends APIChat`, que
 *   passa a ler a classe antes de ela existir. 8 suítes vermelhas, medido em
 *   26/08/2026 ([T010]). A razão completa está no cabeçalho de
 *   `utils/contexto-de-tela.js`.
 *
 * Cada superfície registra o seu no `created()`:
 *
 *   apiChat.setContextoDeExecucaoProvider(provedorDeContextoDeExecucao(this))
 */

/** O contexto de quem não registrou provedor: os guards recusam com motivo. */
export function contextoDeExecucaoVazio() {
  return { vm: null, router: null, store: null, canAccessRoute: null }
}

/**
 * O provedor de uma superfície.
 *
 * Lê `$router`/`$store` no momento da EXECUÇÃO, nunca no registro: entre o
 * `created()` e o clique do usuário no cartão passa uma conversa inteira, e um
 * valor capturado no registro descreveria o estado de outro momento.
 *
 * `$canAccessRoute` viaja AMARRADO à instância — ele é um método do mixin
 * global (`main.js:403`), e passá-lo solto perderia o `this`.
 *
 * @param {Object} vm  Instância Vue da superfície.
 * @returns {Function} `() => ({vm, router, store, canAccessRoute})`
 */
export function provedorDeContextoDeExecucao(vm) {
  return function() {
    if (!vm) return contextoDeExecucaoVazio()
    const podeAcessar =
      typeof vm.$canAccessRoute === 'function'
        ? function(nome) {
            return vm.$canAccessRoute(nome)
          }
        : null
    return {
      vm: vm,
      router: vm.$router || null,
      store: vm.$store || null,
      canAccessRoute: podeAcessar,
    }
  }
}
